/**
 * Paste-a-link routing for the header search box (client half of the YouTube
 * `IUrlRecognizer`, PLAN §3.1). Recognized YouTube URLs jump straight to the archive page;
 * everything else is a search.
 *
 * | Input                                              | Route                     |
 * |----------------------------------------------------|---------------------------|
 * | youtube.com/watch?v=ID (www., m., music.)          | /watch?v=ID               |
 * | youtu.be/ID                                        | /watch?v=ID               |
 * | youtube.com/shorts/ID, /live/ID, /embed/ID, /v/ID  | /watch?v=ID               |
 * | youtube-nocookie.com/embed/ID                      | /watch?v=ID               |
 * | youtube.com/channel/UC…                            | /channel/UC…              |
 * | youtube.com/@handle                                | /search?q=@handle         |
 * | youtube.com/c/name, /user/name                     | /search?q=name            |
 * | anything else (incl. bare ids and invalid URLs)    | /search?q=<input>         |
 *
 * The scheme is optional (`youtu.be/ID` works). Ids are validated with the shared ref rules.
 */
import {
  channelHref,
  parseChannelRef,
  parseVideoRef,
  watchHref,
  isYouTubeChannelId,
  isYouTubeVideoId,
  type ChannelRefString,
  type VideoRefString,
} from '../lib/ref';

export type SearchRoute =
  | { readonly kind: 'watch'; readonly ref: VideoRefString }
  | { readonly kind: 'channel'; readonly ref: ChannelRefString }
  | { readonly kind: 'search'; readonly q: string };

const youTubeHosts = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
]);
const noCookieHosts = new Set(['youtube-nocookie.com', 'www.youtube-nocookie.com']);
const shortHosts = new Set(['youtu.be', 'www.youtu.be']);
const schemeless = /^(?:[a-z0-9-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be)(?:[/?#]|$)/i;

function toUrl(input: string): URL | null {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input)
    ? input
    : schemeless.test(input)
      ? `https://${input}`
      : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

function watch(id: string | undefined): SearchRoute | null {
  if (!id || !isYouTubeVideoId(id)) return null;
  const ref = parseVideoRef(id);
  return ref ? { kind: 'watch', ref: ref.ref } : null;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function recognizeYouTubeUrl(url: URL): SearchRoute | null {
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split('/').filter((s) => s !== '');
  const [first, second] = segments;

  if (shortHosts.has(host)) return watch(first);

  if (noCookieHosts.has(host)) return first === 'embed' ? watch(second) : null;

  if (!youTubeHosts.has(host)) return null;
  if (first === 'watch') return watch(url.searchParams.get('v') ?? undefined);
  if (first === 'shorts' || first === 'live' || first === 'embed' || first === 'v')
    return watch(second);
  if (first === 'channel' && second && isYouTubeChannelId(second)) {
    const ref = parseChannelRef(second);
    return ref ? { kind: 'channel', ref: ref.ref } : null;
  }
  if (first?.startsWith('@') && first.length > 1)
    return { kind: 'search', q: decodeSegment(first) };
  if ((first === 'c' || first === 'user') && second)
    return { kind: 'search', q: decodeSegment(second) };
  return null;
}

export function routeSearchInput(input: string): SearchRoute {
  const trimmed = input.trim();
  const url = toUrl(trimmed);
  return (url ? recognizeYouTubeUrl(url) : null) ?? { kind: 'search', q: trimmed };
}

export function routeHref(route: SearchRoute): string {
  switch (route.kind) {
    case 'watch':
      return watchHref(route.ref);
    case 'channel':
      return channelHref(route.ref);
    case 'search':
      return `/search?q=${encodeURIComponent(route.q)}`;
  }
}
